import { useCallback, useState } from 'react';

import { JSONValue } from '../types/json';

// Helper functions ------------------------------------------------------------
export const computeInternalKey = (namespace: string | null, key: string) =>
  namespace ? `${namespace}:${key}` : key;

export const lsRead = <Type>(key: string, defaultValue?: Type) => {
  const storedValue = window.localStorage.getItem(key);
  return storedValue ? JSON.parse(storedValue) : defaultValue;
};

export const lsWrite = <Type>(key: string, value: Type) => {
  const valueToStore = JSON.stringify(value);
  window.localStorage.setItem(key, valueToStore);
};

// Function overloads to improve type checking of "undefined" values -----------
interface ParamsWithoutDefault {
  namespace: string | null;
  key: string;
}
interface ParamsWithDefault<Type> extends ParamsWithoutDefault {
  defaultValue: Type;
}
interface ParamsWithOptionalDefault<Type> extends ParamsWithoutDefault {
  defaultValue?: Type;
}

function useLocalStorage<Type extends JSONValue>(
  params: ParamsWithoutDefault,
): [Type | undefined, (newValue: Type) => void, () => void];

function useLocalStorage<Type extends JSONValue>(
  params: ParamsWithDefault<Type>,
): [Type, (newValue: Type) => void, () => void];

/**
 * Helper for managing read/writes to localStorage.
 *
 * IMPORTANT NOTE: This implementation only works for the given key being
 * read/written within the same component. IT WON'T REFLECT EXTERNAL CHANGES.
 *
 * @param namespace      - Typically PACKAGE_NAME from common/constants.
 *                         Use `null` to skip key prefixing.
 * @param key            - Key identifier for the value.
 *                         Must be unique within the namespace.
 * @param [defaultValue] - Value to use if none was stored or when value is
 *                         removed.
 */
function useLocalStorage<Type extends JSONValue>({
  namespace,
  key,
  defaultValue,
}: ParamsWithOptionalDefault<Type>) {
  const actualKey = computeInternalKey(namespace, key);

  const [value, setValueState] = useState<Type | undefined>(() => {
    try {
      return lsRead(actualKey, defaultValue);
    } catch (error) {
      console.error(`Error parsing localStorage item ${actualKey}:\n${error}`);
      return defaultValue;
    }
  });

  const setValue = useCallback(
    (newValue: Type) => {
      try {
        lsWrite(actualKey, newValue);
        setValueState(newValue);
      } catch (error) {
        console.error(error);
      }
    },
    [actualKey],
  );

  const removeValue = useCallback(() => {
    window.localStorage.removeItem(actualKey);
    setValueState(defaultValue);
  }, [actualKey, defaultValue]);

  return [value, setValue, removeValue] as const;
}

export { useLocalStorage };
